import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { myFetch } from '@/commons/fetch';

// Types
interface SettingsState {
  settings: {
    digiStorageEmail?: string;
    digiStoragePassword?: string;
    digiStorageToken?: string;
    defaultEmail?: string;
  } | null;
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
}

const initialState: SettingsState = {
  settings: null,
  isLoading: false,
  error: null,
  hasFetched: false,
};

// Async Thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (username: string, { rejectWithValue }) => {
    try {
      const response = await myFetch(`/${username}/settings`);
      return response.data || {};
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch settings');
    }
  },
  {
    condition: (username, { getState }) => {
      const state = getState() as any;
      // Don't dispatch if already loading or already fetched
      if (state.settings.isLoading || state.settings.hasFetched) {
        return false;
      }
      return true;
    },
  }
);

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async (
    { username, settingsData }: { username: string; settingsData: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await myFetch(`/${username}/settings`, {
        method: 'PUT',
        body: JSON.stringify(settingsData),
      });
      return response.data || {};
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update settings');
    }
  }
);

// Slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSettings: (state) => {
      state.settings = null;
      state.error = null;
      state.hasFetched = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch Settings
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        // Set hasFetched to true immediately to prevent duplicate requests
        state.hasFetched = true;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
        state.hasFetched = true;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.hasFetched = true;
      });

    // Update Settings
    builder
      .addCase(updateSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        state.settings = action.payload;
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSettings } = settingsSlice.actions;
export default settingsSlice.reducer;
